/**
 * Money formatting for the Nepal localization pack.
 *
 * Amounts are decimal strings on the wire (never JS floats) and are converted to
 * numbers only for display. Grouping follows the lakh/crore convention
 * (12,34,567.89) which `en-IN` produces natively — no library needed.
 * See ADR-0007 (money & rounding).
 */
import { add, decimal, round, toFixed, ZERO } from "./decimal";

const NPR_LAKH = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NPR_WHOLE = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

export type Money = string; // e.g. "12345.67" — decimal string, 2dp for amounts

export function toNumber(m: Money | number | null | undefined): number {
  if (m === null || m === undefined || m === "") return 0;
  const n = typeof m === "number" ? m : Number(m);
  return Number.isFinite(n) ? n : 0;
}

/** "12,34,567.89" */
export function formatAmount(m: Money | number | null | undefined): string {
  return NPR_LAKH.format(toNumber(m));
}

/** "NPR 12,34,567.89" */
export function formatNPR(m: Money | number | null | undefined): string {
  return "NPR " + formatAmount(m);
}

/** "12,34,568" — for KPI tiles and charts */
export function formatWhole(m: Money | number | null | undefined): string {
  return NPR_WHOLE.format(Math.round(toNumber(m)));
}

/** "12.3L" / "1.2Cr" / "845K" — compact figures for tiles */
export function formatCompact(m: Money | number | null | undefined): string {
  const n = toNumber(m);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e7) return sign + (abs / 1e7).toFixed(2).replace(/\.?0+$/, "") + "Cr";
  if (abs >= 1e5) return sign + (abs / 1e5).toFixed(2).replace(/\.?0+$/, "") + "L";
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(1).replace(/\.?0+$/, "") + "K";
  return sign + abs.toFixed(0);
}

/**
 * Round half away from zero to 2dp — BIS 3.0 amount precision, the same policy as `Money` in .NET.
 *
 * Done on the exact decimal, not the float: `Math.round((x + EPSILON) * 100) / 100` gave
 * `1234567.005 → 1234567` where .NET gives `1234567.01`, and a total that differs from the API by
 * a paisa is the kind of difference somebody reconciles for an afternoon.
 */
export function round2(n: Money | number): number {
  return Number(toFixed(round(decimal(n), 2), 2));
}

/** Sum decimal strings exactly, then round once at 2dp. */
export function sumMoney(values: Array<Money | number>): number {
  return Number(
    toFixed(
      round(
        values.reduce((acc, v) => add(acc, decimal(v)), ZERO),
        2,
      ),
      2,
    ),
  );
}

/** Amount in words (Indian/Nepali system) — used on printed documents. */
export function amountInWords(m: Money | number): string {
  // From the exact 2dp figure, so 1.005 is one rupee and one paisa rather than one rupee.
  const [rupees = "0", fraction = "00"] = toFixed(round(decimal(m), 2), 2)
    .replace("-", "")
    .split(".");
  const n = Number(rupees);
  const paisa = Number(fraction);
  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];
  const two = (x: number): string =>
    x < 20
      ? (ones[x] ?? "")
      : (tens[Math.floor(x / 10)] ?? "") + (x % 10 ? " " + (ones[x % 10] ?? "") : "");
  const three = (x: number): string =>
    x >= 100
      ? (ones[Math.floor(x / 100)] ?? "") + " hundred" + (x % 100 ? " " + two(x % 100) : "")
      : two(x);
  const cents = paisa ? " and " + two(paisa) + " paisa" : "";
  if (n === 0) return "zero rupees" + cents + " only";
  const parts: string[] = [];
  const crore = Math.floor(n / 1e7);
  const lakh = Math.floor((n % 1e7) / 1e5);
  const thousand = Math.floor((n % 1e5) / 1e3);
  const rest = n % 1e3;
  if (crore) parts.push(two(crore) + " crore");
  if (lakh) parts.push(two(lakh) + " lakh");
  if (thousand) parts.push(two(thousand) + " thousand");
  if (rest) parts.push(three(rest));
  return parts.join(" ") + (n === 1 ? " rupee" : " rupees") + cents + " only";
}
